import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {radii, colors, tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled, {css} from 'styled-components'
import {v4 as uuidv4} from 'uuid'

import {Text, text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

type InputProps = Omit<Partial<JSX.IntrinsicElements['input']>, 'onChange'>

type Option<TValue extends string> = InputProps & {
    value: TValue
    label: string
}

export interface Props<TValue extends string> {
    name: string
    value: string
    optionLeft: Option<TValue>
    optionRight: Option<TValue>
    onChange: (value: TValue) => void
    legend: string
}

const leftRadioId = uuidv4()
const rightRadioId = uuidv4()

const OptionsToggle = <TValue extends string>({
    name,
    optionLeft,
    optionRight,
    value,
    onChange,
    legend,
}: Props<TValue>) => {
    const leftRadioRef = React.useRef(null)
    const rightRadioRef = React.useRef(null)

    const keyUpHandler = (
        e: React.KeyboardEvent<HTMLInputElement>,
        ref: React.MutableRefObject<any>
    ) => {
        if (e.code === 'Space') {
            e.preventDefault()
            ref.current.focus()
            ref.current.click()
        }
    }

    return (
        <StyledFieldset>
            <VisuallyHidden as="legend">{legend}</VisuallyHidden>

            <LeftOption
                type="radio"
                name={name}
                id={leftRadioId}
                value={optionLeft.value}
                checked={value === optionLeft.value}
                onChange={(e) => onChange(e.target.value as TValue)}
                onKeyUp={(e) => keyUpHandler(e, rightRadioRef)}
                ref={leftRadioRef}
            />
            <RadioLabel
                as="label"
                htmlFor={leftRadioId}
                data-testid={`option-toggle-${optionLeft.value}`}
            >
                {optionLeft.label}
            </RadioLabel>

            <RightOption
                type="radio"
                name={name}
                id={rightRadioId}
                value={optionRight.value}
                checked={value === optionRight.value}
                onChange={(e) => onChange(e.target.value as TValue)}
                onKeyUp={(e) => keyUpHandler(e, leftRadioRef)}
                ref={rightRadioRef}
            />
            <RadioLabel
                as="label"
                htmlFor={rightRadioId}
                data-testid={`option-toggle-${optionRight.value}`}
            >
                {optionRight.label}
            </RadioLabel>

            <ToggleSwitcher aria-hidden="true" />
        </StyledFieldset>
    )
}

const StyledFieldset = styled.fieldset`
    position: relative;
    z-index: 0;
    display: inline-grid;
    max-width: max-content;
    grid-auto-flow: column;
    align-items: center;
`

const ToggleSwitcher = styled.span`
    display: block;
    width: ${tokens.spacing56};
    color: ${tokens.shade800};
    pointer-events: none;
    background-color: currentcolor;
    border: ${tokens.spacing4} solid currentColor;
    border-radius: ${tokens.spacing18};

    ::after {
        position: relative;
        top: 0;
        left: 0;
        display: block;
        height: ${tokens.spacing18};
        content: '';
        background-color: ${colors.background.default};
        border-radius: ${radii.circle};
        transition: left ${tokens.smoothInOut};
        aspect-ratio: 1;
    }
`
const focusStyles = css`
    border-color: ${tokens.blue700};
`

const RadioLabel = styled(Text)`
    position: relative;
    z-index: 1;
    ${text({variant: 'paragraph-large'})}
    line-height: 1;
    color: ${colors.text.lightMuted};
    cursor: pointer;

    /*
     * The @media(hover: hover) is used to target non touch devices.
     * The :active is needed to maintain the focus styles when
     * the label is clicked and steals the focus from the radio.
    */
    @media (hover: hover) {
        :active ~ ${ToggleSwitcher} {
            ${focusStyles}
        }
    }
`

const StyledOption = styled.input`
    position: absolute;
    width: 100%;
    height: 100%;
    cursor: pointer;
    opacity: 0;
    inset: 0;

    :checked {
        z-index: -1;
    }

    :checked + ${RadioLabel} {
        color: inherit;
        cursor: default;
    }

    :focus ~ ${ToggleSwitcher} {
        ${focusStyles}
    }
`

const LeftOption = styled(StyledOption)`
    + ${RadioLabel} {
        padding-right: ${tokens.spacing16};
        text-align: right;

        ${mq.min('xs')} {
            padding-right: ${tokens.spacing24};
        }
    }
`

const RightOption = styled(StyledOption)`
    + ${RadioLabel} {
        padding-left: ${tokens.spacing16};
        order: 1;

        ${mq.min('xs')} {
            padding-left: ${tokens.spacing24};
        }
    }

    :checked ~ ${ToggleSwitcher} {
        color: ${tokens.pink400};

        &::after {
            left: calc(100% - ${tokens.spacing18});
        }
    }
`

export default OptionsToggle
